import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import API from '../../../services/API'
import PermissionCheck from '../../../helpers/PermissionCheck'
import BigModal from '../../../components/Modals/BigModal'
import GapSeverityChip from '../components/GapSeverityChip'
import MarkGapReviewedDialog from './MarkGapReviewedDialog'
import AssignGapDialog from './AssignGapDialog'

const REVIEWABLE_SOURCE_TYPES = ['policy', 'sop']

const KEY_QUESTIONS = ['Safe', 'Effective', 'Caring', 'Responsive', 'Well-led']

// Older gaps created before fixPath existed still deserve a "fix it" link -
// map their sourceType to the area where the underlying record lives. New
// gaps carry an exact fixPath from the server and never hit this table.
const FALLBACK_FIX_ROUTE = {
  MandatoryTraining: { path: '/employees/mandatory-training', label: 'Open training matrix' },
  DBSCheck: { path: '/dbs-checks', label: 'Open DBS checks' },
  appraisal: { path: '/appraisals/pending-review', label: 'Open appraisals' },
  policy: { path: '/policy-library', label: 'Open Policy Library' },
  sop: { path: '/standard-operating-procedures', label: 'Open SOPs' },
  Meeting: { path: '/meetings', label: 'Open meetings' },
  GovernanceAction: { path: '/governance-actions', label: 'Open governance actions' },
  risk: { path: '/risk-register', label: 'Open Risk Register' },
  incident: { path: '/incidents', label: 'Open incidents' },
  patientFeedback: { path: '/feedback', label: 'Open feedback' },
  MOT: { path: '/fleet', label: 'Open fleet' },
  LOLER: { path: '/fleet', label: 'Open fleet' },
  defect: { path: '/defects', label: 'Open defects' },
  controlledDrugAudit: { path: '/medicines-management', label: 'Open medicines' },
  complianceQuestion: { path: '/cqc-centre/compliance', label: 'Open compliance questions' },
}

export const getFixLink = (gap) => {
  if (gap.fixPath) return { path: gap.fixPath, label: gap.fixLabel || 'Fix this' }
  return FALLBACK_FIX_ROUTE[gap.sourceType] || null
}

const GapsList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [gaps, setGaps] = useState([])
  const [loading, setLoading] = useState(true)
  const [assigningGap, setAssigningGap] = useState(null)
  const [reviewGap, setReviewGap] = useState(null)
  const navigate = useNavigate()

  const status = searchParams.get('status') || 'open'
  const severity = searchParams.get('severity') || ''
  const keyQuestion = searchParams.get('keyQuestion') || ''

  const loadGaps = () => {
    setLoading(true)
    const params = new URLSearchParams({ status, limit: '200' })
    if (severity) params.set('severity', severity)
    if (keyQuestion) params.set('keyQuestion', keyQuestion)
    API.get(`/cqc-centre/gaps?${params.toString()}`)
      .then((res) => {
        if (res.status === 200) setGaps(res.data.gaps)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadGaps()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, severity, keyQuestion])

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
  }

  return (
    <PermissionCheck roles={['Governance']} showAlert>
      <Stack gap={2}>
        <Typography variant="title1">Evidence Gaps</Typography>
        <Typography variant="body2" color="text.secondary">
          Gaps close automatically when the underlying issue is fixed - use the link on each gap
          to go straight to where it can be rectified. Gaps cannot be dismissed.
        </Typography>
        <Stack direction="row" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => updateParam('status', e.target.value)}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              label="Severity"
              value={severity}
              onChange={(e) => updateParam('severity', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Critical">Critical</MenuItem>
              <MenuItem value="Important">Important</MenuItem>
              <MenuItem value="Advisory">Advisory</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Key Question</InputLabel>
            <Select
              label="Key Question"
              value={keyQuestion}
              onChange={(e) => updateParam('keyQuestion', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {KEY_QUESTIONS.map((kq) => (
                <MenuItem key={kq} value={kq}>
                  {kq}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {!loading && gaps.length === 0 && (
          <Alert severity="success">
            No {status} gaps match these filters. New gaps appear automatically if evidence
            expires or falls below target.
          </Alert>
        )}

        <Stack gap={1}>
          {gaps.map((gap) => {
            const fixLink = getFixLink(gap)
            return (
              <Stack
                key={gap._id}
                direction={{ xs: 'column', sm: 'row' }}
                gap={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  <GapSeverityChip severity={gap.severity} />
                  <Stack>
                    <Typography variant="body2">{gap.message}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gap.keyQuestion} - detected {moment(gap.detectedAt).fromNow()}
                      {gap.assignedTo?.firstName
                        ? ` - assigned to ${gap.assignedTo.firstName} ${gap.assignedTo.lastName}`
                        : ''}
                    </Typography>
                  </Stack>
                </Stack>
                {status === 'open' && (
                  <Stack direction="row" gap={1} flexShrink={0}>
                    {fixLink && (
                      <Button
                        size="small"
                        variant="contained"
                        endIcon={<LaunchIcon />}
                        onClick={() => navigate(fixLink.path)}
                      >
                        {fixLink.label}
                      </Button>
                    )}
                    {REVIEWABLE_SOURCE_TYPES.includes(gap.sourceType) && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => setReviewGap(gap)}
                      >
                        Mark Reviewed
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setAssigningGap(gap)}
                    >
                      {gap.assignedTo?.firstName ? 'Reassign' : 'Assign'}
                    </Button>
                  </Stack>
                )}
              </Stack>
            )
          })}
        </Stack>
      </Stack>

      <BigModal autoWidth open={!!assigningGap} onClose={() => setAssigningGap(null)}>
        {assigningGap && (
          <AssignGapDialog
            gap={assigningGap}
            onClose={() => setAssigningGap(null)}
            onSaved={() => {
              setAssigningGap(null)
              loadGaps()
            }}
          />
        )}
      </BigModal>

      <BigModal autoWidth open={!!reviewGap} onClose={() => setReviewGap(null)}>
        {reviewGap && (
          <MarkGapReviewedDialog
            gap={reviewGap}
            onClose={() => setReviewGap(null)}
            onSaved={() => {
              setReviewGap(null)
              loadGaps()
            }}
          />
        )}
      </BigModal>
    </PermissionCheck>
  )
}

export default GapsList
